import React, { useMemo } from "react";
import theme from "../data/theme";
import { TEAM_META, TEAM_HISTORY as PLAY_HISTORY } from "../data/projectionData";
import { PLAYERS } from "../data/players";
import { usePlayerCard } from "../context/PlayerCardContext";
import { getStarters } from "../utils/depthOverrides";
import { safeReadProjlab } from "../utils/projlabStorage";
import { computePosRankMaps } from "../utils/projlabRankings";
import { projectGamesForRow } from "../utils/projectionSource";
import { scoreFromStats, FULL_PPR_LEGACY, pprLabel, buildInitVolume, buildInitAllocs, buildInitRates, projectTeamPlayers } from "../utils/projlabProjection";
import { EditorHeader, StepIndicator } from "./EditorChrome";

// Derive a saved team's total from its stored (lens-invariant) stats under the
// active lens — never trust the stored teamTotal, a stale artifact of its save lens.
function deriveSavedTotal(saved, scoring) {
  const players = saved?.effSummary?.players;
  if (!Array.isArray(players)) return null;
  return players.reduce((s, p) => s + (p.stats ? scoreFromStats(p.stats, p.pos, scoring, p.games ?? 17) : (p.pts || 0)), 0);
}

const { colors, fonts } = theme;

/** "Derrick Henry" → "D. Henry", single names pass through */
const shortName = (name) => {
  if (!name) return "—";
  const parts = name.trim().split(" ");
  if (parts.length < 2) return name;
  return `${parts[0][0]}. ${parts.slice(1).join(" ")}`;
};

// Resolve a projlab row to an auction PLAYERS entry for the PlayerCard —
// String(sleeper_id) match first, else suffix-stripped name match within the
// row's position (same regex family as findPlayerHist). Null when the row has
// no pool entry (depth-only names): those render as plain text, no card.
const stripSuffix = (name) => name.replace(/\s+(Jr\.?|Sr\.?|II|III|IV|V)$/i, "").trim();
export function resolvePoolEntry(row) {
  if (!row?.player) return null;
  const sid = row.sleeperId ?? row.sleeper_id;
  if (sid != null) {
    const byId = PLAYERS.find(pl => String(pl.sleeper_id) === String(sid));
    if (byId) return byId;
  }
  const target = stripSuffix(row.player);
  return PLAYERS.find(pl => (!row.pos || pl.pos === row.pos) && stripSuffix(pl.name) === target) ?? null;
}

// ── 2025 PPR totals (7-starter estimate) for ranking context ─────────────────
// Team keys MUST use this app's abbrevs (TEAM_META: LA/LAC/JAX, not LAR/JAC) —
// the lookups below are strict `t.team === abbrev`. The old "LAR" row never
// matched the Rams, and the phantom "LV2" row was the Chargers' missing entry.
export const TEAM_PPR_2025 = [
  { team: "BAL", pts: 1845 }, { team: "BUF", pts: 1810 }, { team: "DET", pts: 1792 },
  { team: "PHI", pts: 1768 }, { team: "GB",  pts: 1742 }, { team: "MIN", pts: 1725 },
  { team: "CIN", pts: 1710 }, { team: "SF",  pts: 1695 }, { team: "MIA", pts: 1680 },
  { team: "DAL", pts: 1665 }, { team: "LA",  pts: 1648 }, { team: "HOU", pts: 1632 },
  { team: "SEA", pts: 1618 }, { team: "TB",  pts: 1605 }, { team: "KC",  pts: 1592 },
  { team: "IND", pts: 1575 }, { team: "ATL", pts: 1560 }, { team: "JAX", pts: 1545 },
  { team: "PIT", pts: 1528 }, { team: "LV",  pts: 1512 }, { team: "NYJ", pts: 1496 },
  { team: "CHI", pts: 1480 }, { team: "WAS", pts: 1465 }, { team: "NO",  pts: 1450 },
  { team: "NE",  pts: 1430 }, { team: "CLE", pts: 1415 }, { team: "TEN", pts: 1400 },
  { team: "NYG", pts: 1385 }, { team: "CAR", pts: 1368 }, { team: "ARI", pts: 1352 },
  { team: "DEN", pts: 1338 }, { team: "LAC", pts: 1320 },
];

// Rough positional rank from PPR points
function estPosRank(pos, pts) {
  const t = {
    QB:  [340,310,285,265,250,235,220,205,190,175,165,155,145,135,125,115,105,95,85,75],
    RB:  [380,320,280,240,210,185,165,148,133,120,108,97,87,78,70,62,55,48,42,36,31,27,23,20],
    WR:  [380,330,290,260,235,212,192,175,160,147,135,124,114,105,97,90,83,77,72,67,62,58,54,50],
    TE:  [240,200,170,148,130,115,102,92,83,75,68,62,56,50,45],
  };
  const ts = t[pos] || [];
  for (let i = 0; i < ts.length; i++) {
    if (pts >= ts[i]) return i + 1;
  }
  return ts.length + 1;
}

// Build audit items comparing user projections to 2025 actuals
function buildAudit(abbrev, totalPlays, passAtt, passPct, players, allocSummary) {
  const actual2025 = (PLAY_HISTORY[abbrev] || []).find(h => h.year === 2025);
  const starters   = getStarters(abbrev);
  const allocs     = allocSummary?.allocs || {};
  const items = [];

  if (actual2025) {
    items.push({ label: "Total Plays", yours: totalPlays, actual: actual2025.totalPlays, unit: "" });
    items.push({ label: "Pass %", yours: Math.round(passPct), actual: Math.round(actual2025.passPct * 100), unit: "%" });
  }

  const qb = players.find(p => p.slot === "QB1");
  const qbAct = starters["QB1"]?.stats;
  if (qb && qbAct && qbAct.pAtt > 0) {
    const actualTDPct = Math.round((qbAct.pTD / qbAct.pAtt) * 100);
    // "Yours" = the projection's produced TD% (stats.passTD/passAtt). The
    // rates.tdPct field is a seeded display/benchmark rate the QB projection
    // never consumes — passTD derives from receiver recTDs (computeDerivedPass)
    // — and it's absent entirely on teams saved before the Jul 14 QB rate
    // re-baseline, which rendered this row as 0%. Rate kept only as fallback.
    const yoursTDPct = qb.stats?.passAtt > 0
      ? (qb.stats.passTD / qb.stats.passAtt) * 100
      : (qb.rates?.tdPct || 0);
    items.push({ label: `${shortName(qb.player)} TD%`, yours: Math.round(yoursTDPct), actual: actualTDPct, unit: "%" });
  }

  const rb1 = players.find(p => p.slot === "RB1");
  const rb1Act = starters["RB1"]?.stats;
  if (rb1 && rb1Act && actual2025) {
    if (actual2025.rushAtt > 0) {
      const actualShare = Math.round(rb1Act.rAtt / actual2025.rushAtt * 100);
      const userShare   = Math.round(allocs["RB1"]?.rushPct ?? 0);
      items.push({ label: `${shortName(rb1.player)} Rush Share`, yours: userShare, actual: actualShare, unit: "%" });
    }
    if (rb1Act.rAtt > 0) {
      const actualYPC = +(rb1Act.rYds / rb1Act.rAtt).toFixed(1);
      items.push({ label: `${shortName(rb1.player)} YPC`, yours: +(rb1.rates.ypc || 0).toFixed(1), actual: actualYPC, unit: "" });
    }
  }

  const wr1 = players.find(p => p.slot === "WR1");
  const wr1Act = starters["WR1"]?.stats;
  if (wr1 && wr1Act && actual2025) {
    if (actual2025.passAtt > 0) {
      const actualShare = Math.round(wr1Act.tgts / actual2025.passAtt * 100);
      const userShare   = Math.round(allocs["WR1"]?.tgtPct ?? 0);
      items.push({ label: `${shortName(wr1.player)} Tgt Share`, yours: userShare, actual: actualShare, unit: "%" });
    }
    if (wr1Act.tgts > 0) {
      const actualCatch = Math.round(wr1Act.rec / wr1Act.tgts * 100);
      items.push({ label: `${shortName(wr1.player)} Catch%`, yours: Math.round(wr1.rates.catchPct || 0), actual: actualCatch, unit: "%" });
    }
  }

  const te1 = players.find(p => p.slot === "TE1");
  const te1Act = starters["TE1"]?.stats;
  if (te1 && te1Act && actual2025 && actual2025.passAtt > 0) {
    const actualShare = Math.round(te1Act.tgts / actual2025.passAtt * 100);
    const userShare   = Math.round(allocs["TE1"]?.tgtPct ?? 0);
    items.push({ label: `${shortName(te1.player)} Tgt Share`, yours: userShare, actual: actualShare, unit: "%" });
  }

  return items;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function RankBadge({ rank, pos }) {
  const bg    = rank <= 5  ? "rgba(34,197,94,0.2)"    : rank <= 12 ? "rgba(251,191,36,0.16)"
              : rank <= 24 ? "rgba(255,255,255,0.08)"  : "rgba(239,68,68,0.14)";
  const color = rank <= 5  ? "#22c55e" : rank <= 12 ? "#fbbf24" : rank <= 24 ? "#A0AEC0" : "#ef4444";
  return (
    <div style={{ display:"inline-flex", alignItems:"center", background:bg, borderRadius:5, padding:"3px 9px" }}>
      <span style={{ fontSize:13, fontWeight:900, color, fontFamily:fonts.display }}>{pos}{rank}</span>
    </div>
  );
}

function RosterTable({ players, rankMaps }) {
  const [expandedSlot, setExpandedSlot] = React.useState(null);
  const { openPlayerCard } = usePlayerCard();

  const rateLabels = { compPct:"Comp%", ypa:"Y/A", tdPct:"TD%", intPct:"INT%", rushYPC:"Rush YPC",
    ypc:"YPC", rushTDPct:"Rush TD%", catchPct:"Catch%", ypr:"Y/Rec", recTDPct:"Rec TD%" };

  const filtered = players.filter(p => !p.slot.includes("+"));

  // 8 unified stat columns — same widths for every position group
  // Standard:  Cmp  | Att | Yds  | TD | Tgt | Rec | RcYd | RcTD
  // QB label:  Cmp  | Att | Yds  | TD | RuA | RuYd| INT  | RuTD
  const STD_HDRS = ["Cmp","Att","Yds","TD","Tgt","Rec","RcYd","RcTD"];
  const QB_HDRS  = ["Cmp","Att","Yds","TD","RuA","RuYd","INT","RuTD"];

  // 🔴 PER PLAYER, not per 17. A row's stat line is a PARTIAL season — the same
  // ~15 games its points were scored over — so a /17 per-game rate understated
  // every yardage line by the same ~12% the points column was out by.
  const pg = (p, v) => (v != null && v > 0 ? (v / projectGamesForRow(p)).toFixed(1) : null);

  const statCells = (p) => {
    if (p.pos === "QB") return [
      { v:p.stats.comp },
      { v:p.stats.passAtt },
      { v:(p.stats.passYds||0).toLocaleString(), pg:pg(p, p.stats.passYds) },
      { v:p.stats.passTD, hi:true },
      { v:p.stats.rushAtt },
      { v:p.stats.rushYds, pg:pg(p, p.stats.rushYds) },
      { v:p.stats.int, neg:true },
      { v:p.stats.rushTD, hi:true },
    ];
    if (p.pos === "RB") return [
      { v:null },
      { v:p.stats.rushAtt },
      { v:(p.stats.rushYds||0).toLocaleString(), pg:pg(p, p.stats.rushYds) },
      { v:p.stats.rushTD, hi:true },
      { v:p.stats.targets },
      { v:p.stats.rec },
      { v:p.stats.recYds, pg:pg(p, p.stats.recYds) },
      { v:p.stats.recTD, hi:true },
    ];
    return [ // WR / TE
      { v:null }, { v:null }, { v:null }, { v:null },
      { v:p.stats.targets },
      { v:p.stats.rec },
      { v:(p.stats.recYds||0).toLocaleString(), pg:pg(p, p.stats.recYds) },
      { v:p.stats.recTD, hi:true },
    ];
  };

  // Group by position
  const groups = [];
  let cur = null;
  filtered.forEach(p => {
    const gKey = p.pos === "QB" ? "QB" : p.pos === "RB" ? "RB" : p.pos === "WR" ? "WR" : "TE";
    if (!cur || cur.key !== gKey) { cur = { key: gKey, players: [] }; groups.push(cur); }
    cur.players.push(p);
  });

  // Column widths — fixed for table-layout: fixed
  const W = { slot:42, cmp:38, att:38, yds:46, td:32, tgt:36, rec:36, rcyd:44, rctd:38, pts:50, rnk:54 };
  const statWs = [W.cmp, W.att, W.yds, W.td, W.tgt, W.rec, W.rcyd, W.rctd];

  const thStyle = { textAlign:"right", fontSize:11, fontWeight:700, color:"#A0AEC0", padding:"6px 4px", whiteSpace:"nowrap", borderBottom:`1px solid ${colors.border.subtle}` };
  const tdStyle = { textAlign:"right", fontSize:13, fontWeight:700, fontFamily:fonts.display, padding:"7px 4px", borderBottom:`1px solid ${colors.border.subtle}` };

  let rowIdx = 0;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
      {groups.map((g, gi) => {
        const isQB = g.key === "QB";
        const hdrs = isQB ? QB_HDRS : STD_HDRS;
        const pc = colors.pos[g.key] || colors.text.faint;
        return (
          <div key={g.key} style={{ marginTop: gi > 0 ? 20 : 0 }}>
            <table style={{ width:"100%", tableLayout:"fixed", borderCollapse:"collapse", background:colors.bg.raised, borderRadius:10, border:`1px solid ${colors.border.subtle}`, overflow:"hidden" }}>
              <colgroup>
                <col style={{ width:W.slot }} />
                <col />
                {statWs.map((w, i) => <col key={i} style={{ width:w }} />)}
                <col style={{ width:W.pts }} />
                <col style={{ width:W.rnk }} />
              </colgroup>
              <thead>
                <tr style={{ background:"rgba(255,255,255,0.025)" }}>
                  <th style={{ ...thStyle, textAlign:"center", color:pc, fontFamily:fonts.display, fontWeight:800, fontSize:10 }}>{g.key}</th>
                  <th style={{ ...thStyle, textAlign:"left", fontSize:9, fontWeight:700, color:colors.text.faint, letterSpacing:"0.5px" }}>PLAYER</th>
                  {hdrs.map((h, hi) => <th key={hi} style={thStyle}>{h}</th>)}
                  <th style={thStyle}>PTS</th>
                  <th style={{ ...thStyle, textAlign:"center" }}>RNK</th>
                </tr>
              </thead>
              <tbody>
                {g.players.map(p => {
                  const ppc      = colors.pos[p.pos] || colors.text.secondary;
                  const posRank  = rankMaps[p.pos]?.get(`${p.pos}|${p.player}`) ?? estPosRank(p.pos, p.pts);
                  const poolEntry = resolvePoolEntry(p);
                  const cells    = statCells(p);
                  const expanded = expandedSlot === p.slot;
                  const even     = rowIdx++ % 2 === 0;
                  const rowBg    = expanded ? "rgba(255,107,0,0.04)" : even ? "transparent" : "rgba(255,255,255,0.02)";

                  return (
                    <React.Fragment key={p.slot}>
                      <tr onClick={() => setExpandedSlot(expanded ? null : p.slot)} style={{ cursor:"pointer", background:rowBg, transition:"background 0.15s" }}>
                        <td style={{ ...tdStyle, textAlign:"center", padding:"7px 2px" }}>
                          <span style={{ display:"inline-block", fontSize:10, fontWeight:900, fontFamily:fonts.display, color:ppc, background:`${ppc}15`, borderRadius:4, padding:"2px 6px" }}>{p.slot}</span>
                        </td>
                        <td style={{ ...tdStyle, textAlign:"left", fontWeight:700, color:colors.text.primary, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {p.player}
                          {/* Row click expands the slot, so game logs get their own small button */}
                          {poolEntry && (
                            <span
                              title="Game logs"
                              onClick={e => { e.stopPropagation(); openPlayerCard(poolEntry.name, {}, "projection_results"); }}
                              style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", marginLeft:6, width:16, height:16, borderRadius:4, verticalAlign:"middle", background:"rgba(255,255,255,0.08)", color:"#64748b", fontSize:10, fontWeight:700, lineHeight:1, cursor:"pointer", transition:"background 0.15s ease, color 0.15s ease" }}
                              onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,107,0,0.2)"; e.currentTarget.style.color = "#FF6B00"; }}
                              onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; e.currentTarget.style.color = "#64748b"; }}
                            >≡</span>
                          )}
                        </td>
                        {cells.map((c, ci) => (
                          <td key={ci} style={{ ...tdStyle, color: c.v == null ? "transparent" : c.hi ? "#22c55e" : c.neg ? "#ef4444" : colors.text.secondary }}>
                            {c.v == null ? "" : c.v}
                            {c.pg && <div style={{ fontSize:9, fontWeight:500, color:colors.text.faint, lineHeight:1, marginTop:1 }}>{c.pg}/g</div>}
                          </td>
                        ))}
                        <td style={{ ...tdStyle, fontWeight:900, color:ppc }}>
                          {p.pts.toFixed(1)}
                          <div style={{ fontSize:9, fontWeight:500, color:colors.text.faint, lineHeight:1, marginTop:1 }}>{(p.pts/projectGamesForRow(p)).toFixed(1)}/g</div>
                        </td>
                        <td style={{ ...tdStyle, textAlign:"center", padding:"7px 2px" }}>
                          <RankBadge rank={posRank} pos={p.pos} />
                        </td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={12} style={{ padding:"10px 16px 12px 56px", background:"rgba(255,107,0,0.03)", borderBottom:`1px solid ${colors.border.subtle}` }}>
                            <div style={{ display:"flex", flexWrap:"wrap", gap:"4px 14px", marginBottom:8 }}>
                              {hdrs.map((h, hi) => {
                                const c = cells[hi];
                                if (c.v == null) return null;
                                const raw = typeof c.v === "string" ? parseInt(c.v.replace(/,/g, ""), 10) : c.v;
                                const perGame = isNaN(raw) ? null : (raw / projectGamesForRow(p)).toFixed(1);
                                return (
                                  <div key={hi} style={{ fontSize:11, color:colors.text.faint }}>
                                    <span style={{ fontWeight:500 }}>{h}: </span>
                                    <span style={{ fontWeight:700, fontFamily:fonts.display, color: c.hi ? "#22c55e" : c.neg ? "#ef4444" : colors.text.secondary }}>{c.v}</span>
                                    {perGame != null && <span style={{ fontSize:9, color:colors.text.faint, marginLeft:3 }}>({perGame}/g)</span>}
                                  </div>
                                );
                              })}
                            </div>
                            <div style={{ display:"flex", flexWrap:"wrap", gap:"4px 14px", paddingTop:6, borderTop:`1px solid ${colors.border.subtle}` }}>
                              {Object.entries(p.rates || {}).map(([k, v]) => (
                                <div key={k} style={{ fontSize:11, color:colors.text.muted }}>
                                  <span style={{ fontWeight:500 }}>{rateLabels[k] || k}: </span>
                                  <span style={{ color:ppc, fontWeight:700, fontFamily:fonts.display }}>{v}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

function PointsBreakdown({ players }) {
  const groups = ["QB","RB","WR","TE"].map(pos => ({
    label: pos,
    pts:   players.filter(p => p.pos === pos).reduce((s, p) => s + p.pts, 0),
    color: colors.pos[pos],
  }));
  const total = groups.reduce((s, g) => s + g.pts, 0) || 1;

  return (
    <div style={{ background:colors.bg.raised, borderRadius:10, border:`1px solid ${colors.border.subtle}`, padding:"14px 16px" }}>
      <div style={{ fontSize:13, fontWeight:900, color:colors.text.primary, fontFamily:fonts.display, marginBottom:10 }}>Points by Position</div>
      <div style={{ display:"flex", height:28, borderRadius:6, overflow:"hidden", marginBottom:10 }}>
        {groups.map((g, i) => (
          <div key={i} style={{ width:`${(g.pts/total)*100}%`, background:g.color, opacity:0.6, display:"flex", alignItems:"center", justifyContent:"center" }}>
            {(g.pts/total) > 0.1 && <span style={{ fontSize:8, fontWeight:800, color:"#fff", fontFamily:fonts.display }}>{g.label}</span>}
          </div>
        ))}
      </div>
      {groups.map((g, i) => (
        <div key={i} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
          <div style={{ width:8, height:8, borderRadius:2, background:g.color, opacity:0.6 }} />
          <span style={{ fontSize:10, fontWeight:700, color:g.color, fontFamily:fonts.display, width:24 }}>{g.label}</span>
          <div style={{ flex:1, height:4, background:"rgba(255,255,255,0.04)", borderRadius:2 }}>
            <div style={{ height:"100%", width:`${(g.pts/total)*100}%`, background:g.color, borderRadius:2, opacity:0.5 }} />
          </div>
          <span style={{ fontSize:11, fontWeight:800, color:colors.text.primary, fontFamily:fonts.display, width:42, textAlign:"right" }}>{g.pts.toFixed(0)}</span>
          <span style={{ fontSize:9, color:colors.text.faint, width:32, textAlign:"right" }}>{((g.pts/total)*100).toFixed(0)}%</span>
        </div>
      ))}
    </div>
  );
}

function TeamContextBar({ abbrev, teamTotal }) {
  const sorted  = [...TEAM_PPR_2025].sort((a, b) => b.pts - a.pts);
  const maxPts  = Math.max(...sorted.map(t => t.pts), teamTotal);
  const projRank = sorted.filter(t => t.pts > teamTotal).length + 1;
  const tc = TEAM_META[abbrev]?.primaryColor || colors.brand.accent;

  const withProj = [...sorted, { team: abbrev+"'26", pts: teamTotal, proj: true }]
    .sort((a, b) => b.pts - a.pts);

  return (
    <div style={{ background:colors.bg.raised, borderRadius:10, border:`1px solid ${colors.border.subtle}`, padding:"14px 16px" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
        <div>
          <div style={{ fontSize:14, fontWeight:900, color:colors.text.primary, fontFamily:fonts.display }}>Team Offense Ranking</div>
          <div style={{ fontSize:11, color:"#A0AEC0" }}>vs 2025 actual team totals (7-starter PPR)</div>
        </div>
        <div style={{ textAlign:"right" }}>
          <div style={{ fontSize:11, color:"#A0AEC0" }}>Projected Rank</div>
          <div style={{ fontSize:28, fontWeight:900, color:projRank<=3?"#22c55e":projRank<=8?"#fbbf24":colors.text.primary, fontFamily:fonts.display }}>#{projRank}</div>
        </div>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
        {withProj.slice(0, 12).map((t, i) => {
          const w = (t.pts / maxPts) * 100;
          const isProj = t.proj;
          const isSelf25 = t.team === abbrev;
          return (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ width:50, fontSize:11, fontWeight:isProj?900:isSelf25?700:600, color:isProj?colors.brand.accent:isSelf25?tc:"#A0AEC0", textAlign:"right", fontFamily:fonts.display }}>{t.team}</div>
              <div style={{ flex:1, height:18, background:"rgba(255,255,255,0.03)", borderRadius:3, overflow:"hidden" }}>
                <div style={{ height:"100%", borderRadius:3, width:`${w}%`, background:isProj?"linear-gradient(90deg,#FF6B00,#FF9500)":isSelf25?tc:"rgba(255,255,255,0.1)", opacity:isProj?0.8:isSelf25?0.5:0.3, boxShadow:isProj?"0 0 12px rgba(255,107,0,0.3)":"none" }} />
              </div>
              <div style={{ width:38, fontSize:12, fontWeight:isProj?900:600, color:isProj?colors.brand.accent:"#A0AEC0", fontFamily:fonts.display, textAlign:"right" }}>{Math.round(t.pts)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AuditTrail({ abbrev, totalPlays, passAtt, passPct, players, allocSummary }) {
  const items      = buildAudit(abbrev, totalPlays, passAtt, passPct, players, allocSummary);
  const deviations = items.filter(a => Math.abs(a.yours - a.actual) > 0.3);
  const matches    = items.filter(a => Math.abs(a.yours - a.actual) <= 0.3);

  return (
    <div style={{ background:colors.bg.raised, borderRadius:10, border:`1px solid ${colors.border.subtle}`, padding:"14px 16px" }}>
      <div style={{ fontSize:13, fontWeight:900, color:colors.text.primary, fontFamily:fonts.display, marginBottom:4 }}>Decision Audit</div>
      <div style={{ fontSize:10, color:colors.text.faint, marginBottom:10 }}>How your 2026 projection compares to 2025 actual</div>

      {matches.length > 0 && (
        <div style={{ marginBottom:8 }}>
          <div style={{ fontSize:9, fontWeight:700, color:"#22c55e", letterSpacing:"0.5px", marginBottom:4 }}>MATCHED 2025</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
            {matches.map((a, i) => (
              <span key={i} style={{ fontSize:9, color:colors.text.muted, background:"rgba(34,197,94,0.08)", borderRadius:4, padding:"3px 8px" }}>
                {a.label}: {a.yours}{a.unit}
              </span>
            ))}
          </div>
        </div>
      )}

      {deviations.length > 0 && (
        <div>
          <div style={{ fontSize:9, fontWeight:700, color:colors.brand.accent, letterSpacing:"0.5px", marginBottom:4 }}>YOUR CHANGES</div>
          {deviations.map((a, i) => {
            const diff = a.yours - a.actual;
            const up   = diff > 0;
            return (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"4px 0", borderBottom:`1px solid ${colors.border.subtle}` }}>
                <span style={{ fontSize:10, color:colors.text.secondary, flex:1 }}>{a.label}</span>
                <span style={{ fontSize:10, color:colors.text.faint }}>{a.actual}{a.unit}</span>
                <span style={{ fontSize:10, color:colors.text.faint }}>→</span>
                <span style={{ fontSize:11, fontWeight:800, color:colors.brand.accent, fontFamily:fonts.display }}>{a.yours}{a.unit}</span>
                <span style={{ fontSize:9, fontWeight:700, color:up?"#22c55e":"#ef4444" }}>{up?"↑":"↓"}{Math.abs(diff).toFixed(1)}</span>
              </div>
            );
          })}
        </div>
      )}

      {deviations.length === 0 && matches.length > 0 && (
        <div style={{ padding:"8px 12px", background:"rgba(34,197,94,0.06)", borderRadius:6, marginTop:4 }}>
          <div style={{ fontSize:10, color:"#22c55e", fontWeight:600 }}>You projected this team to repeat their 2025 season exactly. Consider where you think things might change for 2026.</div>
        </div>
      )}
    </div>
  );
}

function ChangelogPanel({ changelog, onUpdate }) {
  const [open, setOpen] = React.useState(false);
  const [editIdx, setEditIdx] = React.useState(null);
  const [noteText, setNoteText] = React.useState("");

  if (!changelog || changelog.length === 0) return null;

  const sorted = [...changelog].sort((a, b) => b.ts - a.ts);

  const fmtTime = (ts) => {
    const d = new Date(ts);
    const h = d.getHours(), m = d.getMinutes();
    return `${h % 12 || 12}:${String(m).padStart(2, "0")}${h < 12 ? "a" : "p"}`;
  };

  const startEdit = (origIdx) => {
    const entry = changelog[origIdx];
    setEditIdx(origIdx);
    setNoteText(entry.note || "");
  };

  const saveNote = () => {
    if (editIdx == null) return;
    const updated = changelog.map((e, i) => i === editIdx ? { ...e, note: noteText.trim() || null } : e);
    onUpdate?.(updated);
    setEditIdx(null);
    setNoteText("");
  };

  return (
    <div style={{ background:colors.bg.raised, borderRadius:10, border:`1px solid ${colors.border.subtle}`, padding:"14px 16px" }}>
      <div onClick={() => setOpen(!open)} style={{ display:"flex", alignItems:"center", cursor:"pointer", gap:8 }}>
        <div style={{ fontSize:13, fontWeight:900, color:colors.text.primary, fontFamily:fonts.display, flex:1 }}>
          Change Log
          <span style={{ fontSize:10, fontWeight:600, color:colors.text.faint, marginLeft:6 }}>{changelog.length} edit{changelog.length !== 1 ? "s" : ""}</span>
        </div>
        <span style={{ fontSize:12, color:colors.text.faint, transition:"transform 0.15s", transform:open ? "rotate(180deg)" : "rotate(0)" }}>▼</span>
      </div>

      {open && (
        <div style={{ marginTop:10, display:"flex", flexDirection:"column", gap:0 }}>
          {sorted.map((entry, si) => {
            const origIdx = changelog.indexOf(entry);
            const isEditing = editIdx === origIdx;
            return (
              <div key={si} style={{ padding:"6px 0", borderBottom:`1px solid ${colors.border.subtle}` }}>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ fontSize:9, color:colors.text.faint, width:36, flexShrink:0, fontFamily:fonts.display }}>{fmtTime(entry.ts)}</span>
                  <span style={{ fontSize:10, fontWeight:700, color:colors.brand.accent, flexShrink:0 }}>{entry.playerOrStep}</span>
                  <span style={{ fontSize:10, color:colors.text.muted, flex:1, minWidth:0 }}>
                    {entry.field}: <span style={{ color:colors.text.faint }}>{entry.oldValue}</span>
                    <span style={{ color:colors.text.faint }}> → </span>
                    <span style={{ fontWeight:700, fontFamily:fonts.display, color:colors.text.primary }}>{entry.newValue}</span>
                  </span>
                  <span
                    onClick={(e) => { e.stopPropagation(); startEdit(origIdx); }}
                    style={{ cursor:"pointer", fontSize:11, color:entry.note ? colors.brand.accent : colors.text.faint, flexShrink:0, padding:"2px 4px" }}
                    title={entry.note || "Add note"}
                  >✎</span>
                </div>
                {entry.note && !isEditing && (
                  <div style={{ marginLeft:42, fontSize:10, color:colors.brand.accent, fontStyle:"italic", marginTop:2 }}>"{entry.note}"</div>
                )}
                {isEditing && (
                  <div style={{ marginLeft:42, marginTop:4, display:"flex", gap:4 }}>
                    <input
                      autoFocus
                      value={noteText}
                      onChange={e => setNoteText(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") saveNote(); if (e.key === "Escape") setEditIdx(null); }}
                      placeholder="e.g. Pickens hamstring injury"
                      style={{ flex:1, fontSize:10, padding:"4px 8px", borderRadius:4, border:`1px solid ${colors.border.default}`, background:colors.bg.elevated, color:colors.text.primary, outline:"none" }}
                    />
                    <button onClick={saveNote} style={{ fontSize:9, fontWeight:700, padding:"4px 10px", borderRadius:4, border:"none", background:colors.brand.accent, color:"#fff", cursor:"pointer", fontFamily:fonts.display }}>Save</button>
                    <button onClick={() => setEditIdx(null)} style={{ fontSize:9, fontWeight:600, padding:"4px 8px", borderRadius:4, border:`1px solid ${colors.border.default}`, background:"transparent", color:colors.text.muted, cursor:"pointer" }}>✕</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const STEP_LABELS = ["Plays","Split","Alloc","Eff"];

function MiniProgress({ completedSteps, tc }) {
  return (
    <div style={{ display:"flex", gap:2, flexShrink:0 }}>
      {STEP_LABELS.map((l, i) => (
        <div key={i} style={{ width:16, height:4, borderRadius:2, background: completedSteps?.[i] ? tc : "rgba(255,255,255,0.08)", opacity: completedSteps?.[i] ? 0.7 : 1 }} title={l} />
      ))}
    </div>
  );
}

function readTeamStatus(teamAbbrev, scoring) {
  try {
    const saved = safeReadProjlab(teamAbbrev);
    if (!saved) return null;
    const steps = saved.completedSteps || [false, false, false, false];
    const allDone = steps.every(Boolean);
    const anyDone = steps.some(Boolean);
    const ppr = deriveSavedTotal(saved, scoring);
    if (allDone) return { state:"done", completedSteps:steps, ppr };
    if (anyDone) return { state:"progress", completedSteps:steps, ppr:null };
    return null;
  } catch { return null; }
}

function NextTeamFlow({ abbrev, teamTotal, onProjectTeam, onViewAll, scoring }) {
  const meta     = TEAM_META[abbrev];
  const division = meta?.division;
  const divPeers = Object.values(TEAM_META)
    .filter(t => t.division === division)
    .sort((a, b) => a.fullName.localeCompare(b.fullName));

  // Read localStorage status for each peer (memoized once on mount)
  const peerStatus = React.useMemo(() => {
    const map = {};
    divPeers.forEach(t => {
      if (t.abbrev === abbrev) {
        map[t.abbrev] = { state:"done", completedSteps:[true,true,true,true], ppr:teamTotal };
      } else {
        map[t.abbrev] = readTeamStatus(t.abbrev, scoring);
      }
    });
    return map;
  }, [divPeers, abbrev, teamTotal, scoring]);

  const divDone = divPeers.every(t => peerStatus[t.abbrev]?.state === "done");

  return (
    <div style={{ background:colors.bg.raised, borderRadius:10, border:`1px solid ${colors.border.subtle}`, padding:"14px 16px" }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
        <div style={{ fontSize:13, fontWeight:900, color:colors.text.primary, fontFamily:fonts.display, flex:1 }}>Continue Projecting</div>
        {divDone && (
          <span style={{ fontSize:9, fontWeight:800, color:"#2dd4bf", background:"rgba(45,212,191,0.12)", border:"1px solid rgba(45,212,191,0.2)", padding:"1px 7px", borderRadius:10, fontFamily:fonts.display }}>COMPLETE</span>
        )}
      </div>
      <div style={{ fontSize:10, color:colors.text.faint, marginBottom:8 }}>{division}</div>
      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
        {divPeers.map((t, i) => {
          const tc = t.primaryColor;
          const status = peerStatus[t.abbrev];
          const isDone = status?.state === "done";
          const isProgress = status?.state === "progress";
          const isSelf = t.abbrev === abbrev;
          return (
            <div key={i}
              onClick={() => !isSelf && !isDone && onProjectTeam?.(t.abbrev)}
              style={{
                display:"flex", alignItems:"center", gap:10, padding:"10px 14px", borderRadius:8,
                cursor: isSelf || isDone ? "default" : "pointer",
                background: isDone ? "rgba(34,197,94,0.06)" : isProgress ? `${tc}08` : "rgba(255,255,255,0.02)",
                border:`1px solid ${isDone ? "rgba(34,197,94,0.15)" : isProgress ? `${tc}20` : colors.border.subtle}`,
                borderLeft:`3px solid ${tc}`,
                opacity: isDone && !isSelf ? 0.8 : 1,
                transition:"all 0.15s",
              }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ fontSize:13, fontWeight:800, color:colors.text.primary, fontFamily:fonts.display }}>{t.abbrev}</span>
                  <span style={{ fontSize:11, color:colors.text.muted, fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t.fullName}</span>
                </div>
                {isDone && (
                  <div style={{ fontSize:9, color:"#22c55e", marginTop:2 }}>
                    Completed{status.ppr != null ? ` · ${status.ppr.toFixed(1)} PPR` : ""}
                  </div>
                )}
              </div>
              {isDone && (
                <div style={{ width:18, height:18, borderRadius:"50%", background:"rgba(34,197,94,0.15)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <span style={{ fontSize:11, color:"#22c55e", fontWeight:900 }}>✓</span>
                </div>
              )}
              {isProgress && (
                <>
                  <div style={{ width:6, height:6, borderRadius:"50%", background:tc, flexShrink:0, animation:"pulse-dot 1.8s ease-in-out infinite", boxShadow:`0 0 6px ${tc}60` }} />
                  <MiniProgress completedSteps={status.completedSteps} tc={tc} />
                </>
              )}
              {!isDone && !isProgress && (
                <button onClick={(e) => { e.stopPropagation(); onProjectTeam?.(t.abbrev); }} style={{ padding:"6px 16px", borderRadius:6, border:"none", cursor:"pointer", background:"rgba(255,255,255,0.08)", color:colors.text.secondary, fontSize:10, fontWeight:700, fontFamily:fonts.display }}>
                  PROJECT →
                </button>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ marginTop:10 }}>
        <button onClick={onViewAll} style={{ width:"100%", padding:"12px 0", borderRadius:8, border:"none", cursor:"pointer", background:"linear-gradient(135deg,#FF6B00,#E55A00)", color:"#fff", fontSize:11, fontWeight:800, fontFamily:fonts.display, letterSpacing:"1px", boxShadow:"0 4px 12px rgba(255,107,0,0.3)" }}>
          VIEW ALL 32 TEAMS →
        </button>
      </div>
    </div>
  );
}

// ── ResultsView ───────────────────────────────────────────────────────────────

export function ResultsView({ abbrev, resumedFromSave, teamStarters, teamColor, totalPlays, passAtt, rushAtt, passPct, players: rawPlayers, allocSummary, changelog, onChangelogUpdate, onBack, onEditStep, onOpenRankings, onProjectTeam, progress, scoring = FULL_PPR_LEGACY }) {
  const meta = TEAM_META[abbrev];
  const tc   = teamColor || meta?.primaryColor || colors.brand.accent;

  // Derive-on-read: re-score stored stats under the active lens; the stored pts
  // is a stale artifact of the save-time lens. teamTotal is the derived sum.
  const players = useMemo(
    () => rawPlayers.map(p => (p.stats ? { ...p, pts: scoreFromStats(p.stats, p.pos, scoring, p.games ?? 17) } : p)),
    [rawPlayers, scoring]
  );
  const teamTotal = useMemo(() => players.reduce((s, p) => s + (p.pts || 0), 0), [players]);

  const rankMaps = useMemo(() => computePosRankMaps(progress || {}, scoring), [progress, scoring]);

  // Default-seed total for this one team under the CURRENT lens, seeded from
  // the SAME teamStarters the wizard and its Δ VS DEFAULTS chips use — the
  // semantic is "the defaults you'd get rebuilding this team today" (the
  // rankings field keeps its STARTERS_2025 path). Stats are generated by the
  // live default recipe (pure builders + projectTeamPlayers), then TOTALIZED
  // the same way "yours" is (scoreFromStats over the rounded per-player
  // stats) — live calcPPR and scoreFromStats diverge ~1% under bonus lenses,
  // so method parity is what makes an untouched save read Δ = 0.0.
  // Display-only comparison line; never persisted.
  const defaultSeedTotal = useMemo(() => {
    if (!resumedFromSave) return null;
    const ts = teamStarters ?? getStarters(abbrev);
    if (!ts) return null;
    const { totalPlays: tp, passPct: pp } = buildInitVolume(abbrev);
    const defAllocs = buildInitAllocs(abbrev, ts);
    const defRates = buildInitRates(abbrev, defAllocs, ts);
    const { players: defPlayers } = projectTeamPlayers({ abbrev, totalPlays: tp, passPct: pp, allocs: defAllocs, rates: defRates, teamStarters: ts, scoring });
    return defPlayers.reduce((s, p) => s + (p.stats ? scoreFromStats(p.stats, p.pos, scoring, p.games ?? 17) : (p.pts || 0)), 0);
  }, [resumedFromSave, teamStarters, abbrev, scoring]);

  // Find the 2025 actual total for delta display
  const actual2025 = (PLAY_HISTORY[abbrev] || []).find(h => h.year === 2025);
  const actualPPR  = TEAM_PPR_2025.find(t => t.team === abbrev)?.pts;
  const delta      = actualPPR != null ? teamTotal - actualPPR : null;

  const projRank   = TEAM_PPR_2025.filter(t => t.pts > teamTotal).length + 1;

  const wrapStyle = {
    minHeight: "100vh",
    background: `radial-gradient(ellipse at 50% 0%, #1a1f35 0%, ${colors.bg.deep} 50%, ${colors.bg.app} 100%)`,
    fontFamily: fonts.body,
    color: colors.text.primary,
    padding: "20px",
  };

  return (
    <div style={wrapStyle}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>

        {/* Unified header + step nav — same chrome as the four step pages, so
            the results page reads as part of the flow, not a dead end. No active
            tab (activeIdx past the last step); all four remain clickable and
            route back via onEditStep. onViewResults is null (already here). */}
        <EditorHeader
          abbrev={abbrev}
          teamColor={tc}
          onBack={onBack}
          onOpenRankings={onOpenRankings}
          onViewResults={null}
        />
        <StepIndicator activeIdx={4} maxIdx={4} onStepClick={onEditStep} />

        {/* Hero */}
        <div style={{ background:`linear-gradient(135deg, ${tc}12, rgba(255,107,0,0.06))`, borderRadius:14, border:`1px solid ${tc}20`, padding:"20px 24px", marginBottom:16, display:"flex", alignItems:"center", gap:20 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:10, fontWeight:700, color:colors.text.faint, letterSpacing:"2px", fontFamily:fonts.display }}>PROJECTED TEAM TOTAL</div>
            <div style={{ fontSize:42, fontWeight:900, color:colors.brand.accent, fontFamily:fonts.display, lineHeight:1, marginTop:4 }}>{teamTotal.toFixed(1)}</div>
            <div style={{ fontSize:12, color:colors.text.muted, marginTop:4 }}>{pprLabel(scoring)} fantasy points · 7-starter output</div>
            {defaultSeedTotal != null && (() => {
              const d = defaultSeedTotal - teamTotal;
              return (
                <div style={{ fontSize:11, color:colors.text.faint, marginTop:3 }}>
                  default-seed total: {defaultSeedTotal.toFixed(1)} ({d >= 0 ? "+" : ""}{d.toFixed(1)} vs yours)
                </div>
              );
            })()}
          </div>
          <div style={{ display:"flex", gap:20 }}>
            {delta != null && (
              <div style={{ textAlign:"center" }}>
                <div style={{ fontSize:8, color:colors.text.faint }}>vs '25 actual</div>
                <div style={{ fontSize:20, fontWeight:900, color:delta>=0?"#22c55e":"#ef4444", fontFamily:fonts.display }}>
                  {delta>=0?"+":""}{delta.toFixed(0)}
                </div>
              </div>
            )}
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:8, color:colors.text.faint }}>Proj Rank</div>
              <div style={{ fontSize:20, fontWeight:900, color:projRank<=3?"#22c55e":projRank<=8?"#fbbf24":colors.text.primary, fontFamily:fonts.display }}>
                #{projRank}
              </div>
            </div>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:8, color:colors.text.faint }}>Per Week</div>
              {/* 🔴 THIS 17 STAYS. It is the NFL season, not a projection's
                  normalisation: the OFFENSE plays 17 games, and teamTotal
                  already carries each player's own availability inside it. A
                  per-player divisor here would answer "what if nobody missed
                  a game", which is not this team's weekly output. */}
              <div style={{ fontSize:20, fontWeight:900, color:colors.text.primary, fontFamily:fonts.display }}>
                {(teamTotal / 17).toFixed(1)}
              </div>
            </div>
          </div>
        </div>

        {/* Two-column layout */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 340px", gap:14, alignItems:"start" }}>

          {/* LEFT — roster table */}
          <div>
            <div style={{ fontSize:12, fontWeight:800, color:colors.text.primary, fontFamily:fonts.display, marginBottom:6 }}>Player Projections</div>
            <RosterTable players={players} rankMaps={rankMaps} />
          </div>

          {/* RIGHT — insight panels */}
          <div style={{ position:"sticky", top:16, display:"flex", flexDirection:"column", gap:10 }}>
            <PointsBreakdown players={players} />
            <TeamContextBar abbrev={abbrev} teamTotal={teamTotal} />
            <AuditTrail abbrev={abbrev} totalPlays={totalPlays} passAtt={passAtt} passPct={passPct} players={players} allocSummary={allocSummary} />
            <ChangelogPanel changelog={changelog} onUpdate={onChangelogUpdate} />
            <NextTeamFlow abbrev={abbrev} teamTotal={teamTotal} onProjectTeam={onProjectTeam} onViewAll={onBack} scoring={scoring} />
          </div>
        </div>
      </div>
    </div>
  );
}
